/**
 * Spring Physics Animation
 *
 * Pure math module for spring-based animation. No DOM, no RAF — just
 * computes the next state given current state and elapsed time.
 *
 * Uses a damped harmonic oscillator model:
 *   F = -stiffness * displacement - damping * velocity
 *   acceleration = F / mass
 */

// =============================================================================
// Types
// =============================================================================

export interface SpringConfig {
	/** Spring stiffness (default: 170) */
	stiffness: number;
	/** Damping coefficient (default: 26) */
	damping: number;
	/** Mass (default: 1) */
	mass: number;
	/** Velocity threshold to consider settled (default: 0.01) */
	restThreshold: number;
}

export interface SpringState {
	/** Current value */
	value: number;
	/** Current velocity */
	velocity: number;
}

export const DEFAULT_SPRING_CONFIG: SpringConfig = {
	stiffness: 170,
	damping: 26,
	mass: 1,
	restThreshold: 0.01,
};

// =============================================================================
// Core Spring Physics
// =============================================================================

/**
 * Advance a spring one time step using semi-implicit Euler integration.
 *
 * @param state - Current spring state (value + velocity)
 * @param target - Target value the spring is pulling toward
 * @param config - Spring configuration
 * @param dt - Time step in seconds (typically 1/60 for 60fps)
 * @returns New spring state
 */
export function stepSpring(
	state: SpringState,
	target: number,
	config: SpringConfig,
	dt: number,
): SpringState {
	const displacement = state.value - target;
	const springForce = -config.stiffness * displacement;
	const dampingForce = -config.damping * state.velocity;
	const acceleration = (springForce + dampingForce) / config.mass;

	const newVelocity = state.velocity + acceleration * dt;
	const newValue = state.value + newVelocity * dt;

	return { value: newValue, velocity: newVelocity };
}

/**
 * Check if a spring has settled (close enough to target with low velocity).
 */
export function isSpringSettled(
	state: SpringState,
	target: number,
	config: SpringConfig,
): boolean {
	const displacement = Math.abs(state.value - target);
	const velocity = Math.abs(state.velocity);
	return displacement < config.restThreshold && velocity < config.restThreshold;
}

// =============================================================================
// Multi-dimensional Spring (for camera x, y, zoom)
// =============================================================================

export interface Spring3State {
	x: SpringState;
	y: SpringState;
	zoom: SpringState;
}

export interface Spring3Target {
	x: number;
	y: number;
	zoom: number;
}

/**
 * Create initial spring state from current values (zero velocity).
 */
export function createSpring3State(current: Spring3Target): Spring3State {
	return {
		x: { value: current.x, velocity: 0 },
		y: { value: current.y, velocity: 0 },
		zoom: { value: current.zoom, velocity: 0 },
	};
}

/**
 * Advance all three springs one time step.
 */
export function stepSpring3(
	state: Spring3State,
	target: Spring3Target,
	config: SpringConfig,
	dt: number,
): Spring3State {
	return {
		x: stepSpring(state.x, target.x, config, dt),
		y: stepSpring(state.y, target.y, config, dt),
		zoom: stepSpring(state.zoom, target.zoom, config, dt),
	};
}

/**
 * Check if all three springs have settled.
 */
export function isSpring3Settled(
	state: Spring3State,
	target: Spring3Target,
	config: SpringConfig,
): boolean {
	return (
		isSpringSettled(state.x, target.x, config) &&
		isSpringSettled(state.y, target.y, config) &&
		isSpringSettled(state.zoom, target.zoom, config)
	);
}

/**
 * Extract current values from spring state.
 */
export function getSpring3Values(state: Spring3State): Spring3Target {
	return {
		x: state.x.value,
		y: state.y.value,
		zoom: state.zoom.value,
	};
}
